// General survey types that can be used across different survey implementations

export enum SURVEY_TRIGGER {
  ONBOARDING = 'onboarding',
  ONBOARDING_WEB = 'onboarding_web',
  CHANGE_STAGE = 'change_stage',
  IM_READY_PATH_END = 'im_ready_path_end',
  IM_READY_MY_PATH = 'im_ready_my_path',
  IM_READY_GUIDED_PATHS = 'im_ready_guided_paths',
}

export enum SURVEY_NAME {
  NEW_ONBOARDING = 'new_onboarding',
  REACTIVATION_ONBOARDING = 'reactivation_onboarding',
  CHANGE_STAGE = 'change_stage',
  END_OF_PATH = 'end_of_path',
}

export enum EQuestionTypes {
  todayDescription = 'user_state',
  menopauseSymptoms = 'Menopause',
  dueDate = 'due_date',
  otherStage = 'other_description',
  drCheck = 'diastasis_recti',
  fullActivity = 'cleared_for_activity',
  recoveringFromSurgery = 'recovering_from_surgery',
  activityLevel = 'activity_level',
  opf = 'opf',
  prolapse = 'prolapse',
  goal = 'main_goal',
  otherGoal = 'goal_other',
  timeGoal = 'goal_commitment',
}

export enum ETodayDescriptionChoices {
  pregnant = 'pregnant',
  less_6 = 'less_6_months_pp',
  more_6 = 'more_6_months_pp',
  planning = 'planning',
  menopause = 'menopause',
  other = 'other',
}

export enum EProgram {
  prepare = 'prepare',
  reclaim = 'reclaim',
  surpass = 'surpass',
}

export enum ESurveysTypes {
  onboarding = 'onboarding',
  symptom_tracking = 'symptom_tracking',
  demographic = 'demographic',
  post_purchase = 'post_purchase',
  change_stage = 'change_stage',
}

export type TSurveyElement = {
  name: string;
  title: string;
  options?: Array<{
    value: string;
    text: string;
  }>;
  type?: string;
  required?: boolean;
  skip?: boolean;
  multiline?: boolean;
  oneButton?: boolean;
  choices?: { value: string; text: string }[];
  columns?: { value: string; text: string }[];
  rows?: { value: string; text: string }[];
};

export type TSurveyPage = {
  name: string;
  title: string;
  elements: Array<TSurveyElement>;
};

export type TSurveyPages = Array<TSurveyPage>;

export type TAnswer = string | string[] | boolean | Record<string, string | boolean | null>;

export type TAnswers<TQuestionType extends string> = Record<TQuestionType, TAnswer>;

export interface ISurveyState<TQuestionType extends string> {
  data: Record<TQuestionType, TSurveyElement> | null;
  currentItem: TSurveyElement | null;
  currentQuestion: TQuestionType | null;
  prevQuestions: Array<TQuestionType>;
  answers: TAnswers<TQuestionType> | null;
  error: string;
  title: string;
  surveyId: string;
  survey: TSurveyPages | null;
}

export type TPath = {
  id: string;
  name: string;
  fullyComplete?: boolean;
  description?: string;
};

export type TStage = {
  id: string;
  name: string;
  title?: boolean;
  description?: string;
};

export type StatusCurrentPath = 'in_progress' | 'completed' | 'recommended';
export type Question = Record<string, object>;

export enum SURVEY_TYPES {
  ONBOARDING = 'onboarding',
  ONBOARDING_WEB = 'onboarding_web',
  CHANGE_STAGE = 'change_stage',
  SYMPTOM_TRACKER = 'symptom_tracking',
  DEMOGRAPHIC = 'demographic',
  POST_PURCHASE = 'post_purchase',
}

export enum PATH_CHANGE_TYPE {
  GUIDED_PATHS_SELECT = 'GUIDED_PATHS_SELECT',
  GUIDED_PATHS_START_OVER = 'GUIDED_PATHS_START_OVER',
  GUIDED_PATHS_RESUME = 'GUIDED_PATHS_RESUME',
  GUIDED_PATHS_OPEN_COMPLETED = 'GUIDED_PATHS_OPEN_COMPLETED',
  PATH_END_START_OVER = 'PATH_END_START_OVER',
  PATH_END_WHERE_NEXT = 'PATH_END_WHERE_NEXT',
  CHANGE_STAGE_SURVEY_AUTO_PATH = 'CHANGE_STAGE_SURVEY_AUTO_PATH',
  CHANGE_STAGE_OPEN_COMPLETED = 'CHANGE_STAGE_OPEN_COMPLETED',
  CHANGE_STAGE_START_OVER = 'CHANGE_STAGE_START_OVER',
  ONBOARDING_SURVEY_AUTO_PATH = 'ONBOARDING_SURVEY_AUTO_PATH',
  IM_READY_PATH_END = 'IM_READY_PATH_END',
  IM_READY_GUIDED_PATHS = 'IM_READY_GUIDED_PATHS',
  IM_READY_MY_PATH = 'IM_READY_MY_PATH',
  ADMIN_SELECT = 'ADMIN_SELECT',
}

export enum USER_STATE_TYPE {
  NEW = 'new',
  REACTIVATION = 'reactivation',
  MEMBER = 'member',
  EXISTING = 'existing',
}

export type DemographicAnswers = Record<string, string>;
export type QuestionAnswers<T> = Record<string, T>;
export type SurveyTimesAnswered = Record<string, number>;
export type TimesAnswered = Record<SURVEY_TYPES, SurveyTimesAnswered>;

interface IPath {
  ID: number;
}

interface IPathStatus {
  fully_complete: boolean;
}

export interface IPathInfo {
  description: string;
  id: string;
  name: string;
  stageId: string;
  _typename?: string;
  image?: string;
  fullyComplete?: boolean;
}

export interface ISurveyRequestData {
  type: SURVEY_TYPES;
  version: number;
}

export interface ISurveyPageData {
  onboardingSurveyVersion: number;
  changeStageSurveyVersion: number;
  path: IPath | null;
  pathStatus: IPathStatus | null;
  isReactivated: boolean;
  shouldIntro: boolean;
  activation_cycle_count: number;
  personalization_count: number;
  shouldShowDemographic: boolean;
  times_answered: TimesAnswered;
}

export enum SurveyPersonalizedName {
  NEW_ONBOARDING = 'new_onboarding',
  REACTIVATION_ONBOARDING = 'reactivation_onboarding',
  END_OF_PATH = 'end_of_path',
  CHANGE_STAGE = 'change_stage',
}

interface ISurveyItems {
  id: string;
  surveyJson: string;
  type: string;
  __typename: string;
}

export type ISurveys = Array<ISurveyItems>;

export interface IUserAnswers {
  activity_level?: string;
  cleared_for_activity?: boolean;
  diastasis_recti?: boolean;
  symptoms?: string[] | boolean;
  user_state?: string;
  due_date?: string;
  other_description?: string;
}

export interface ISTAnswers {
  'core_&_pelvic_strength': {
    core_strength: number;
    pelvic_strength: number;
  };
  symptoms: {
    low_back_pain: number;
    urinary_leaking: number;
    vaginal_heaviness: number;
    diastasis_recti: number;
    pelvic_pain: number;
  };
}

export interface IAdditionalSurveyInfo {
  type: string;
  lastAnswerAt: number;
  stage: string;
  number: number;
  cycle: number;
  checkin: boolean;
}

export type IAdditionalSurveysInfo = Array<IAdditionalSurveyInfo>;

export type ListOptions = {
  name: string | number;
  value: string | boolean | null;
  getType: () => string;
};

export type AdditionalEventData = Record<string, string | number | boolean | object>;

export type SurveyPageQuestionChoice = {
  description: string;
  jsonObj: Record<string, string>;
};

export type SurveyPageQuestionElement = {
  name: string;
  title: string;
  value: ListOptions[];
  choices: SurveyPageQuestionChoice[];
};

export type SurveySender = {
  currentPage: { elements: SurveyPageQuestionElement[] };
  data: TAnswers<EQuestionTypes>;
  pageNextText: string;
  pagePrevText: string;
  currentPageNo: number;
};

type PropertyHash = {
  name: string;
  navigationButtonsVisibility: string;
};

type SurveyOptionsPage = {
  title: string;
  name: string;
  propertyHash: PropertyHash;
  questions: ListOptions[];
  getValue: () => ListOptions[];
};

export type SurveyOptions = {
  newCurrentPage: SurveyOptionsPage;
  oldCurrentPage: SurveyOptionsPage;
  page: SurveyOptionsPage;
  showDataSavingError: () => void;
  allowChanging: boolean;
};

export interface SurveyHandlers {
  handleSurveyComplete: (id: string, sender: SurveySender, options: SurveyOptions) => Promise<void>;
  handleCurrentPageChanging: (sender: SurveySender, options: SurveyOptions) => void;
  handleCurrentPageChanged: (sender: SurveySender, options: SurveyOptions) => void;
  handleValueChanged: (sender: SurveySender, options: SurveyOptions) => void;
}

export interface ISurveyProps {
  surveys: ISurveys;
  surveyName: string;
  handleAfterRenderPage: (sender: SurveySender, options: SurveyOptions) => void;
  handleCurrentPageChanged: (sender: SurveySender, options: SurveyOptions) => void;
  handleCurrentPageChanging: (sender: SurveySender, options: SurveyOptions) => void;
  handleValueChanged: (sender: SurveySender, options: SurveyOptions) => void;
  onSurveyComplete: (
    id: string,
    sender: SurveySender,
    options: SurveyOptions,
  ) => Promise<IPathInfo | void>;
  isDueDateOTC?: boolean;
}

export type ParsedSurvey = {
  pages: TSurveyPages;
};

export interface SurveyTrackingData {
  survey_name: string;
  activation_cycle_count: number;
  survey_meta: {
    survey_version: number;
    source: string;
    survey_trigger: SURVEY_TRIGGER;
  };
}
